// One recipe at its own URL, /recipes/:id (redesign slice 4: wireframes 9
// and 26, styled as board 29). For reading and deciding; cooking has its own
// layout at /recipes/:id/cook. No photo yet, so the page uses the design's
// collapsed (no-photo) state.
//
// The servings stepper only changes what the page shows. "Save at N
// servings" in the menu writes the new size to the recipe for everyone, as
// the old ½× / 2× buttons did.
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { SharedByNotice } from "../components/RecipeSharing";
import { RecipeShareSheet } from "../components/RecipeShareSheet";
import { PrintPortal } from "../components/PrintPortal";
import { AddToPlanPanel } from "../components/AddToPlanPanel";
import { MoreMenu, type MoreMenuItem } from "../components/MoreMenu";
import { Chips, IngredientList, Section, ServingsStepper, StepList } from "../components/RecipeParts";
import { supporting } from "../components/recipeStyles";
import { COLORS, DIET_TAG_LABELS, FONT_SIZE, LAYOUT, RADIUS, RECIPE_TYPE_LABELS, SPACE } from "../lib/constants";
import { capitalizeFirst, formatWeekRange } from "../lib/utils";
import { ingredientLines, scaleFactor, stepLines, timeParts, upcomingPlacements } from "../lib/recipeDisplay";
import type { Recipe } from "../lib/types";

const isYieldType = (r: Recipe) => r.recipeType === "preserves" || r.recipeType === "medicinal";

export function RecipePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const mp = useMealPlanner();
  const [panel, setPanel] = useState<null | "plan" | "share">(null);
  const [servings, setServings] = useState<number | null>(null); // null: the recipe's own
  const [confirmDelete, setConfirmDelete] = useState(false);
  const recipe = mp.recipeById(id);
  const placements = useMemo(
    () => (recipe ? upcomingPlacements(mp.plansByWeek, recipe.id, mp.planDefaults.weekStartDay, new Date()) : []),
    [recipe, mp.plansByWeek, mp.planDefaults.weekStartDay]
  );

  if (!recipe) {
    return (
      <div className="mp-sans" style={{ maxWidth: 560 }}>
        <p style={{ fontSize: FONT_SIZE.base, color: COLORS.soft }}>This recipe isn't in your library. It may have been removed, or a share was turned off.</p>
        <Link to="/recipes" style={{ color: COLORS.accent, fontWeight: 700, fontSize: FONT_SIZE.base }}>
          Go to Recipes
        </Link>
      </div>
    );
  }

  const isOwner = recipe.ownerUserId === mp.userId;
  const canEdit = mp.canEditRecipeContent(recipe);
  const favorite = mp.isFavorite(recipe);
  const shown = servings ?? recipe.servings;
  const factor = isYieldType(recipe) ? 1 : scaleFactor(recipe.servings, shown);
  const ingredients = ingredientLines(recipe.ingredients || [], factor);
  const steps = stepLines(recipe.instructions || []);
  const times = timeParts(recipe);
  const cameFromApp = (window.history.state?.idx ?? 0) > 0;

  const menuItems: MoreMenuItem[] = [];
  // Editing is the four writing steps (slice 7b), at their own URL.
  if (canEdit) menuItems.push({ label: "Edit", onSelect: () => navigate(`/recipes/${recipe.id}/edit`) });
  menuItems.push({ label: "Print", onSelect: () => window.print() });
  if (canEdit && factor !== 1) {
    menuItems.push({
      label: `Save at ${shown} servings`,
      onSelect: () => {
        mp.scaleRecipe(recipe, factor);
        setServings(null);
      },
    });
  }
  const removeLabel = isOwner ? "Delete recipe" : "Remove from my library";
  menuItems.push(
    confirmDelete
      ? {
          label: isOwner ? "Delete for good" : "Yes, remove it",
          danger: true,
          onSelect: () => {
            mp.deleteRecipe(recipe.id);
            navigate("/recipes", { replace: true });
          },
        }
      : { label: removeLabel, danger: true, keepOpen: true, onSelect: () => setConfirmDelete(true) }
  );

  const meta = [
    RECIPE_TYPE_LABELS[recipe.recipeType as keyof typeof RECIPE_TYPE_LABELS] || recipe.recipeType,
    ...(recipe.mealTypes || []).map((m) => capitalizeFirst(m)),
    recipe.cuisine || "",
  ].filter(Boolean);
  const dietChips = (recipe.dietTags || []).includes("vegan")
    ? [DIET_TAG_LABELS.vegan]
    : (recipe.dietTags || []).includes("vegetarian")
      ? [DIET_TAG_LABELS.vegetarian]
      : [];
  if ((recipe.dietTags || []).includes("glutenFree")) dietChips.push(DIET_TAG_LABELS.glutenFree);
  const chips = [...dietChips, ...(recipe.seasons || []), ...(recipe.cookingMethod || [])];
  const nutrition = [
    [recipe.caloriesPerServing, "", "calories"],
    [recipe.proteinPerServing, " g", "protein"],
    [recipe.carbsPerServing, " g", "carbs"],
    [recipe.fatPerServing, " g", "fat"],
  ] as const;
  const hasNutrition = nutrition.some(([n]) => (n || 0) > 0);

  const buttonBase = { minHeight: 52, borderRadius: RADIUS.md, fontSize: FONT_SIZE.base, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", padding: "0 var(--space-x3)", cursor: "pointer" } as const;

  return (
    <article className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3, maxWidth: 1000, marginBottom: SPACE.x4 }}>
      {/* Back, and the recipe's own actions */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md }}>
        <button
          type="button"
          className="mp-btn"
          onClick={() => (cameFromApp ? navigate(-1) : navigate("/recipes"))}
          style={{ minHeight: LAYOUT.tapTarget, padding: 0, background: "transparent", color: COLORS.accent, fontSize: FONT_SIZE.base, fontWeight: 700 }}
        >
          ‹ {cameFromApp ? "Back" : "Recipes"}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: SPACE.md }}>
          {isOwner && (
            <button
              type="button"
              className="mp-btn"
              aria-expanded={panel === "share"}
              onClick={() => setPanel((p) => (p === "share" ? null : "share"))}
              style={{ minHeight: LAYOUT.tapTarget, padding: "0 var(--space-xxl)", borderRadius: RADIUS.pill, border: `1.5px solid ${COLORS.accent}`, background: "transparent", color: COLORS.accent, fontSize: FONT_SIZE.base, fontWeight: 700 }}
            >
              Share
            </button>
          )}
          <button
            type="button"
            className="mp-btn"
            aria-pressed={favorite}
            aria-label={favorite ? "Remove from favorites" : "Mark as favorite"}
            title={favorite ? "Remove from favorites" : "Mark as favorite"}
            onClick={() => mp.toggleFavorite(recipe.id)}
            style={{ width: LAYOUT.tapTarget, height: LAYOUT.tapTarget, borderRadius: RADIUS.pill, border: `1px solid ${COLORS.line}`, background: COLORS.card, color: favorite ? COLORS.accent : COLORS.soft, fontSize: FONT_SIZE.h3, lineHeight: 1, padding: 0 }}
          >
            {favorite ? "★" : "☆"}
          </button>
          <MoreMenu label={`Recipe menu: ${menuItems.map((m) => m.label.toLowerCase()).join(", ")}`} items={menuItems} onClose={() => setConfirmDelete(false)} />
        </div>
      </div>

      {/* Title and facts */}
      <header style={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.display, fontWeight: 600, lineHeight: 1.1, color: COLORS.ink }}>
          {recipe.title || "Untitled recipe"}
        </h1>
        <div style={supporting}>
          {(recipe.source || recipe.sourceUrl) && (
            <>
              From <SourceText source={recipe.source || ""} link={recipe.sourceUrl || ""} />
              {meta.length > 0 && " · "}
            </>
          )}
          {meta.join(" · ")}
        </div>
        {times.total && (
          <div style={{ fontSize: FONT_SIZE.base }}>
            {times.parts.join(" · ")}
            {times.parts.length > 1 && " · "}
            {times.parts.length > 1 && <strong>{times.total}</strong>}
          </div>
        )}
        <Chips items={chips} />
      </header>

      {/* The two main actions */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: SPACE.lg, maxWidth: 560 }}>
        <Link to={`/recipes/${recipe.id}/cook`} style={{ ...buttonBase, background: COLORS.accent, color: COLORS.onAccent }}>
          Cook this
        </Link>
        <button
          type="button"
          className="mp-btn"
          aria-expanded={panel === "plan"}
          onClick={() => setPanel((p) => (p === "plan" ? null : "plan"))}
          style={{ ...buttonBase, background: "transparent", color: COLORS.accent, border: `1.5px solid ${COLORS.accent}` }}
        >
          Add to plan
        </button>
      </div>
      {panel === "plan" && (
        <AddToPlanPanel
          recipe={recipe}
          weekData={mp.weekData}
          weekStartDay={mp.planDefaults.weekStartDay}
          weekLabel={`the week of ${formatWeekRange(mp.weekStart)}`}
          onAssign={mp.assignRecipeToPlan}
          onRemove={mp.removeRecipeFromPlan}
        />
      )}
      {panel === "share" && isOwner && <RecipeShareSheet recipe={recipe} onClose={() => setPanel(null)} />}

      {placements.length > 0 && (
        <div style={{ background: COLORS.accentTint, borderRadius: RADIUS.xl, padding: "var(--space-xl) var(--space-xl2)" }}>
          <div style={{ fontWeight: 700 }}>In your plan</div>
          <div style={supporting}>{placements.map((p) => p.label).join(", ")}</div>
        </div>
      )}

      {/* Ingredients beside instructions on a wide screen */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: isMobile ? SPACE.x3 : SPACE.x5, alignItems: "flex-start" }}>
        <div style={{ flex: "2 1 300px", minWidth: 0 }}>
          <Section
            title="Ingredients"
            aside={isYieldType(recipe) ? <span style={supporting}>Yields {recipe.yield || "—"}</span> : <ServingsStepper servings={shown} onChange={(n) => setServings(n === recipe.servings ? null : n)} />}
          >
            {factor !== 1 && <span style={supporting}>Scaled from {recipe.servings} servings. The recipe itself is unchanged.</span>}
            <IngredientList lines={ingredients} />
          </Section>
        </div>
        <div style={{ flex: "3 1 380px", minWidth: 0, maxWidth: "65ch" }}>
          <Section title="Instructions">
            <StepList lines={steps} />
          </Section>
        </div>
      </div>

      {hasNutrition && (
        <Section title="Nutrition per serving">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: SPACE.md, maxWidth: 480 }}>
            {nutrition.map(([n, unit, label]) => (
              <div key={label} style={{ borderTop: `1px solid ${COLORS.line}`, paddingTop: SPACE.sm }}>
                <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg, fontVariantNumeric: "tabular-nums" }}>{n ? `${Math.round(n)}${unit}` : "—"}</div>
                <div style={supporting}>{label}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <footer style={{ borderTop: `1px solid ${COLORS.line}`, paddingTop: SPACE.xl }}>
        {isOwner ? (
          <p style={{ ...supporting, margin: 0 }}>Yours · {recipe.isPublic ? "public, anyone can find it" : "private to you and the people you share it with"}</p>
        ) : (
          <SharedByNotice recipe={recipe} colors={mp.colors} />
        )}
      </footer>

      <RecipePrint recipe={recipe} servings={shown} factor={factor} />
    </article>
  );
}

// A recipe's source: its name, linked when there is a source link (0056),
// or the link's site name when there is no name. An older recipe whose
// source text is itself a web address shows as a link too.
function SourceText({ source, link }: { source: string; link: string }) {
  const parse = (s: string) => {
    try {
      return /^https?:\/\//i.test(s) ? new URL(s) : null;
    } catch {
      return null;
    }
  };
  const url = parse(link) ?? parse(source);
  if (!url) return <span style={{ color: COLORS.ink }}>{source}</span>;
  const label = source && !parse(source) ? source : url.hostname.replace(/^www\./, "");
  return (
    <a href={url.href} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent, fontWeight: 700 }}>
      {label}
    </a>
  );
}

// What the Print menu item prints (see PrintPortal): the recipe at the size
// on screen, in plain black on white.
function RecipePrint({ recipe, servings, factor }: { recipe: Recipe; servings: number; factor: number }) {
  const times = timeParts(recipe);
  return (
    <PrintPortal>
      <div style={{ fontSize: FONT_SIZE.h2, fontWeight: 700, marginBottom: SPACE.xs }}>{recipe.title || "Untitled recipe"}</div>
      {(recipe.source || recipe.sourceUrl) && <div style={{ fontSize: FONT_SIZE.sm, fontStyle: "italic", marginBottom: SPACE.sm, overflowWrap: "anywhere" }}>Source: {[recipe.source, recipe.sourceUrl].filter(Boolean).join(", ")}</div>}
      <div style={{ fontSize: FONT_SIZE.base, marginBottom: SPACE.x3 }}>
        {[...times.parts, times.parts.length > 1 ? `${times.total} total` : "", isYieldType(recipe) ? `Yields ${recipe.yield || "—"}` : `Serves ${servings}`].filter(Boolean).join(" · ")}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: SPACE.x4, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: FONT_SIZE.base, fontWeight: 700, textTransform: "uppercase", marginBottom: SPACE.md }}>Ingredients</div>
          {ingredientLines(recipe.ingredients || [], factor).map((l) => (
            <div key={l.key} style={{ fontSize: FONT_SIZE.base, padding: "var(--space-hair) 0", fontWeight: l.kind === "header" ? 700 : 400, fontStyle: l.kind === "note" ? "italic" : "normal" }}>
              {l.kind === "header" ? l.label : l.kind === "note" ? l.text : [l.amount, l.text].filter(Boolean).join(" ")}
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: FONT_SIZE.base, fontWeight: 700, textTransform: "uppercase", marginBottom: SPACE.md }}>Instructions</div>
          {stepLines(recipe.instructions || []).map((l) => (
            <div key={l.key} style={{ fontSize: FONT_SIZE.base, padding: "var(--space-hair) 0", fontWeight: l.kind === "header" ? 700 : 400, fontStyle: l.kind === "note" ? "italic" : "normal" }}>
              {l.kind === "header" ? l.label : l.kind === "note" ? `— ${l.text}` : `${l.number}. ${l.text}`}
            </div>
          ))}
        </div>
      </div>
    </PrintPortal>
  );
}
